import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import {
  BaseEdge,
  Background,
  Controls,
  Handle,
  Position,
  ReactFlow,
  getBezierPath,
  type Edge,
  type EdgeProps,
  type Node,
  type NodeProps,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { EmptyState } from './EmptyState';
import { EngineIcon } from './EngineIcon';
import { RefreshButton } from './RefreshButton';
import { engineFromDoEngine, type TargetEngine } from '../lib/targetEngine';
import {
  CANVAS_MAX_EDGES,
  HEALTH_STYLE,
  asHealth,
  buildFlow,
  healthSummary,
  neverScanned,
  rowsFor,
  type ClusterGraph,
  type EdgeHealth,
} from '../lib/clusterGraph';

/**
 * The glyph for one database node. The server sends `engine: 'atlas'` for an Atlas
 * project and a DO engine string otherwise (contract §1); the `db:atlas:` id prefix is
 * the fallback, because a node whose engine the scan never learned must still be
 * tellable apart from a Postgres firewall.
 */
function engineOf(id: string, engine: string | null | undefined): TargetEngine {
  if (engine === 'atlas' || id.startsWith('db:atlas:')) return 'atlas';
  return engineFromDoEngine(engine);
}

/**
 * The Databases tab of a cluster.
 *
 * The question it answers is the one that used to take four tables by hand: which
 * managed databases is this cluster ALLOWED to reach, and is that permission actually
 * working? The colour of a line is the health of the permission (see `lib/clusterGraph`
 * for the vocabulary), and every colour is repeated as a WORD in the table below —
 * the canvas is the overview, the table is the answer, and nothing is stated in colour
 * alone.
 *
 * Two rendering rules are load-bearing rather than cosmetic:
 *
 *  1. Custom nodes with HANDLES. `ServiceGraph`'s `ClusterGroupNode` is a container
 *     with no handles because containment expresses its edge; here the edge IS the
 *     statement, so both ends need a handle or React Flow draws the line from the
 *     node's centre and it crosses the box.
 *  2. Never a canvas drawn from half the facts. If the `do-db-firewall-scan` has never
 *     run on this environment there are no firewall rules in the database at all, and a
 *     graph without them reads as "this cluster reaches nothing" — a wrong answer, not
 *     a missing one. In that state the banner NAMES THE JOB and stands in for the
 *     canvas (AC-7); the table below still lists the access lists we did read.
 */
/** Exported because `ClusterDetailPage` asks the SAME question when `cluster(name)`
 *  is null: a cluster known only from its agents still has a graph, and the focus the
 *  server synthesizes (`cl:name:<name>`, sub "not in inventory") is what the page
 *  renders its reduced header from (contract §2/§7b, AC-2). One document, so the two
 *  callers cannot drift apart — and Apollo's cache serves the second read for free. */
export const CLUSTER_GRAPH = gql`
  query ClusterGraph($cluster: String!) {
    clusterGraph(cluster: $cluster) {
      doScanAt
      focus {
        id
        kind
        label
        sub
      }
      nodes {
        id
        kind
        label
        sub
        engine
        team
        targetKey
      }
      edges {
        id
        source
        target
        verb
        confidence
        health
        reason
        agentsGood
        agentsTotal
        nodesTotal
        evidence
        listReadAt
      }
    }
  }
`;

interface Data {
  clusterGraph: ClusterGraph | null;
}

/** The cluster, on the left. `sub` is the region; a cluster the scanners have never
 *  seen still gets a node, so the tab shows the question even with no answer. */
function FocusNode({ data }: NodeProps) {
  const d = data as { label: string; sub?: string | null; nodeId: string };
  return (
    <div
      className="gnode-focus"
      data-testid="graph-node"
      data-node-id={d.nodeId}
      data-health="neutral"
      title={d.label}
    >
      <Handle type="source" position={Position.Right} isConnectable={false} />
      <div className="gnode-focus-name">{d.label}</div>
      <div className="gnode-focus-sub">{d.sub || 'not in inventory'}</div>
    </div>
  );
}

/** One managed database or Atlas project. The engine glyph is the only thing that
 *  tells a Postgres firewall from a Valkey one at a glance. */
function DbNode({ data }: NodeProps) {
  const d = data as {
    label: string;
    team?: string | null;
    engine?: string | null;
    kind?: string;
    health: EdgeHealth;
    nodeId: string;
  };
  const engine = engineOf(d.nodeId, d.engine);
  return (
    <div
      className="gnode-db"
      data-testid="graph-node"
      data-node-id={d.nodeId}
      data-health={d.health}
      title={d.team ? `${d.label} · ${d.team}` : d.label}
    >
      <Handle type="target" position={Position.Left} isConnectable={false} />
      <EngineIcon engine={engine} />
      <span className="gnode-db-text">
        <span className="gnode-db-name">{d.label}</span>
        {d.team && <span className="gnode-db-team">{d.team}</span>}
      </span>
      <span className="gnode-health-dot" data-health={d.health} aria-hidden="true" />
    </div>
  );
}

/**
 * The edge. A custom type rather than `style` on a default edge for one reason: the
 * health has to be READABLE from the DOM (`[data-testid="graph-edge"][data-health]`),
 * because that is how the criteria — and QC — assert on the colour without sampling
 * pixels. The `<g>` wrapper carries it; `BaseEdge` draws the path.
 */
function HealthEdge(props: EdgeProps) {
  const { sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, data, id } = props;
  const [path] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });
  const d = (data ?? {}) as { health?: string; edgeId?: string; reason?: string };
  const health = asHealth(d.health);
  const style = HEALTH_STYLE[health];
  return (
    <g data-testid="graph-edge" data-edge-id={d.edgeId ?? id} data-health={health}>
      <title>{d.reason || health}</title>
      <BaseEdge
        id={id}
        path={path}
        style={{ stroke: style.stroke, strokeDasharray: style.dash, strokeWidth: 1.6 }}
      />
    </g>
  );
}

const nodeTypes = { clusterFocus: FocusNode, clusterDb: DbNode };
const edgeTypes = { health: HealthEdge };

export function ClusterDatabasesGraph({
  clusterName,
  clusterUuid,
}: {
  clusterName: string;
  /** `raw.clusterId`. Missing means DigitalOcean `k8s` rules cannot be matched to this
   *  cluster at all — a real gap in the answer, so the tab says so rather than
   *  reporting fewer databases without explanation. */
  clusterUuid?: string | null;
}) {
  const { data, loading, error, refetch } = useQuery<Data>(CLUSTER_GRAPH, {
    variables: { cluster: clusterName },
    fetchPolicy: 'cache-and-network',
  });
  const [showGraph, setShowGraph] = useState(false);

  const graph = data?.clusterGraph ?? null;
  const rows = useMemo(() => rowsFor(graph), [graph]);
  const flow = useMemo(() => buildFlow(graph), [graph]);
  const summary = useMemo(() => healthSummary(graph?.edges), [graph]);

  // Past 40 permissions the fan is unreadable, so the table leads and the canvas is
  // opt-in. The threshold is a judgement, not a measurement — it is the point where
  // the 320px canvas can no longer show a row per line without zooming out to soup.
  const tableFirst = rows.length > CANVAS_MAX_EDGES;
  // The never-scanned banner stands INSTEAD OF the canvas (AC-7): a graph drawn from
  // half the facts invites people to read the missing half as "nothing there". The
  // table below still lists the Atlas access lists and mappings we did read.
  const scanMissing = neverScanned(graph);
  const canvasOpen = !scanMissing && (!tableFirst || showGraph);

  return (
    <div className="cluster-graph">
      <div className="section-head">
        <h3>Databases ({rows.length})</h3>
        <span className="graph-legend cluster-graph-legend" data-testid="graph-legend">
          <span className="lg green">— healthy</span>
          <span className="lg red">— unhealthy</span>
          <span className="lg amber">— unknown nodes</span>
          <span className="lg neutral">╌ no evidence</span>
        </span>
        <span className="spacer" />
        {tableFirst && !scanMissing && (
          <button
            type="button"
            className="mini-btn"
            data-testid="graph-canvas-toggle"
            onClick={() => setShowGraph((v) => !v)}
          >
            {showGraph ? 'Hide graph' : 'Show graph'}
          </button>
        )}
        <RefreshButton refetch={() => refetch().catch(() => undefined)} />
      </div>

      {/* The never-scanned banner sits ABOVE everything, including the empty state:
          it explains why the rest of the tab is thin. */}
      {scanMissing && (
        <div className="graph-banner" data-testid="never-scanned-banner">
          <span>
            DigitalOcean firewalls have never been scanned on this environment, so
            <code> k8s</code> and <code> ip_addr</code> rules are missing here. Run{' '}
            <strong>do-db-firewall-scan</strong> to fill them in — until then this tab shows only
            Atlas access lists and mappings.
          </span>
          <Link to="/scheduler" className="btn-primary">
            Open Scheduler
          </Link>
        </div>
      )}

      {clusterUuid == null && (
        <p className="muted text-2xs" data-testid="uuid-unknown-hint">
          cluster uuid unknown — DigitalOcean k8s rules cannot be matched (ip rules still are)
        </p>
      )}

      {/* The error paragraph replaces neither the head nor the rows: with
          `cache-and-network` a failed REFRESH must not blank a table that is still
          showing the last good answer (the RefreshButton contract). */}
      {error && <p className="muted">Failed to load: {error.message}</p>}

      {loading && !data ? (
        <p className="muted">Loading…</p>
      ) : rows.length === 0 ? (
        !error && (
          <EmptyState
            icon="inbox"
            title="No database allows this cluster"
            body={
              <>
                No DigitalOcean firewall rule, Atlas access-list entry or mapping names this
                cluster. Register one on the{' '}
                <Link to="/cluster-access/mappings">cluster → target mappings</Link> page, or run
                the scans if the estate has changed.
              </>
            }
            actionLabel="Cluster access"
            actionTo="/cluster-access/mappings"
          />
        )
      ) : (
        <>
          {canvasOpen && (
            <div className="graph-wrap">
              <ReactFlow
                nodes={flow.nodes as unknown as Node[]}
                edges={flow.edges as unknown as Edge[]}
                nodeTypes={nodeTypes}
                edgeTypes={edgeTypes}
                fitView
                minZoom={0.1}
                proOptions={{ hideAttribution: true }}
              >
                <Background />
                <Controls showInteractive={false} />
              </ReactFlow>
            </div>
          )}

          <table data-testid="cluster-db-table">
            <thead>
              <tr>
                <th>Database</th>
                <th>Engine</th>
                <th>Team</th>
                <th>Health</th>
                <th>Agents</th>
                <th>Nodes</th>
                <th>Reason</th>
                <th>Evidence</th>
                <th>As of</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const engine = engineOf(r.id, r.engine);
                return (
                  <tr
                    key={r.edgeId}
                    data-testid="cluster-db-row"
                    data-db-id={r.id}
                    data-health={r.health}
                  >
                    <td>
                      <span className="ip-cell">
                        <EngineIcon engine={engine} />
                        {/* No link when the database is not a registered target — a
                            dead link teaches people the row is broken. */}
                        {r.href ? <Link to={r.href}>{r.name}</Link> : <span>{r.name}</span>}
                      </span>
                    </td>
                    <td className="muted">{engine}</td>
                    <td className="muted">{r.team ?? '—'}</td>
                    <td>
                      {/* TEXT, always. The dot is decoration; the word is the value. */}
                      <span className={`badge health-${r.health}`} data-testid="health-badge">
                        {r.badge}
                      </span>
                    </td>
                    <td className="muted">{r.agents}</td>
                    <td className="muted">{r.nodesTotal ?? '—'}</td>
                    <td className="muted">{r.reason}</td>
                    <td className="muted">{r.evidence}</td>
                    <td className="muted text-2xs">
                      {r.listReadAt ? new Date(r.listReadAt).toLocaleString() : 'never read'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <p className="muted text-2xs" style={{ marginTop: 8 }}>
            {summary.green} healthy · {summary.red} unhealthy · {summary.amber} with nodes running
            no agent · {summary.neutral} with no evidence. An edge means the cluster is{' '}
            <em>allowed</em> by a firewall rule or access list — not that anything is connected.
          </p>
        </>
      )}
    </div>
  );
}
